import React, { createContext, useCallback, useEffect, useState } from "react";
import {
  createUserWithEmailAndPassword,
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  sendPasswordResetEmail,
  updateProfile,
} from "firebase/auth";
import app from "../firebase/firebase.config";
import axios from "axios";
import Loading from "../Pages/Shared/Loading/Loading";
import toast from "react-hot-toast";

export const AuthContext = createContext();
const auth = getAuth(app);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const userAgent = navigator.userAgent;
  const [loading, setLoading] = useState(true);
  const [userInfo, setUserInfo] = useState(() => {
    // Initialize userInfo from localStorage if available
    const storedUserInfo = localStorage.getItem("userInfo");
    try {
      return storedUserInfo ? JSON.parse(storedUserInfo) : {};
    } catch {
      return {};
    }
  });

  const createUser = (email, password) => {
    setLoading(true);
    return createUserWithEmailAndPassword(auth, email, password);
    // const createUserFunction = createUserWithEmailAndPassword(auth, email, password);
    // const unsubscribe = onAuthStateChanged(
    //   auth,
    //   (currentUser) => {
    //     setUser(currentUser);

    //     setLoading(false);

    //   },
    //   []
    // );
    // unsubscribe();
    // return createUserFunction;
  };

  //Adding image and name
  const updateUserProfile = (profile) => {
    return updateProfile(auth.currentUser, profile);
  };

  //Logout
  const logOut = () => {
    setLoading(true);

    try {
      if (userInfo?.email) {
        const userDevice = axios.put(
          `${process.env.REACT_APP_SERVERLESS_API}/api/v1/users/removeDevice/${userInfo?.email}`,
          { device: userAgent }
        );
      }
    } catch (error) {
      console.error("Error removing device:", error);
    }

    return signOut(auth);
  };

  // SECURITY FIX: Enhanced logout function for auth failures
  const handleLogout = async () => {
    try {
      setLoading(true);

      // Clear all localStorage data
      localStorage.removeItem('role');
      localStorage.removeItem('userInfo');
      localStorage.removeItem('token');

      // Reset all auth state
      setUser(null);
      setUserInfo(null);

      // Remove device if user info exists
      if (userInfo?.email) {
        try {
          await axios.put(
            `${process.env.REACT_APP_SERVERLESS_API}/api/v1/users/removeDevice/${userInfo?.email}`,
            { device: userAgent }
          );
        } catch (deviceError) {
          console.error("Error removing device during logout:", deviceError);
        }
      }

      // Sign out from Firebase
      await signOut(auth);

      console.log("User logged out successfully");
    } catch (error) {
      console.error("Logout error:", error);

      // Force cleanup even if Firebase logout fails
      localStorage.clear();
      setUser(null);
      setUserInfo(null);
    } finally {
      setLoading(false);
    }
  };

  const devicelogOut = () => {
    setLoading(true);
    return signOut(auth);
  };
  const forgotPassword = () => {
    sendPasswordResetEmail(auth, auth.currentUser.email)
      .then(() => {
        toast.success("A Password Reset Link has been sent to your email.");
      })
      .catch((error) => {
        toast.error(error.message);
      });
  };

  // sign in with email and password
  const signIn = (email, password) => {
    setLoading(true);

    return signInWithEmailAndPassword(auth, email, password);
  };

  // Login with provider
  const providerLogin = (provider) => {
    setLoading(true);
    return signInWithPopup(auth, provider);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(
      auth,
      (currentUser) => {
        console.log("Auth state changed, current user:", currentUser);
        setUser(currentUser);

        // SECURITY FIX: Clear localStorage if no Firebase user but localStorage has data
        if (!currentUser) {
          const hasStoredUserInfo = localStorage.getItem('userInfo') || localStorage.getItem('role');
          if (hasStoredUserInfo) {
            console.log("No Firebase user but localStorage has user data, clearing...");
            localStorage.removeItem('role');
            localStorage.removeItem('userInfo');
            localStorage.removeItem('token');
            setUserInfo(null);
            toast.info("Session expired. Please login again.");
          }
        }

        setLoading(false);
      },
      []
    );

    return () => {
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    // Don't validate email if user is null (during auth state restoration)
    if (user === null) {
      return;
    }

    // SECURITY FIX: Validate email before API call (only after user is set)
    if (!user?.email || user?.email === 'undefined' || user?.email === 'null' || !user?.email.includes('@')) {
      console.log("Invalid email detected, logging out user:", user?.email);
      setUserInfo(null);
      handleLogout();
      return;
    }

    const fetchUserData = async () => {
      try {
        // Check if we already have userInfo in localStorage for this user
        const storedUserInfo = localStorage.getItem("userInfo");
        if (storedUserInfo) {
          try {
            const parsedUserInfo = JSON.parse(storedUserInfo);
            if (parsedUserInfo?.email === user?.email) {
              console.log("Using cached user data from localStorage");
              setUserInfo(parsedUserInfo);
              return;
            }
          } catch (error) {
            console.error("Error parsing stored user info:", error);
            localStorage.removeItem("userInfo");
          }
        }

        const response = await axios.get(
          `${process.env.REACT_APP_SERVERLESS_API}/api/v1/users?email=${user?.email}`
        );
        const userData = response?.data;

        // SECURITY FIX: Check if user exists in database
        if (!userData || userData?.isUser === false || !userData?._id) {
          console.log("User not found in database, this shouldn't happen with new DB-first registration flow:", user?.email);
          // User is in Firebase but not in DB - this indicates either:
          // 1. Old registration (before DB-first fix) that failed at DB step
          // 2. Manual Firebase user creation without DB record
          // Don't logout here - let the login pages handle this with their modal
          // Just set userInfo to null and let the page-level handlers show the modal
          setUserInfo(null);
          return;
        }

        setUserInfo(userData);

        // Save user data to localStorage for persistence across page refreshes
        localStorage.setItem("role", userData?.role);
        localStorage.setItem("userInfo", JSON.stringify(userData));
      } catch (error) {
        console.error("Error fetching user data:", error);
        toast.error("Failed to verify user. Logging out for security.");
        Loading().close();
        await handleLogout();
      }
    };

    fetchUserData();
  }, [user]);
  // useEffect(() => {
  //   if (userInfo?.role !== "admin" && userInfo?.devices && !userInfo.devices.includes(userAgent)) {
  //     devicelogOut();
  //   }
  // }, [userInfo?.devices])
  const authInfo = {
    user,
    userInfo,
    setUserInfo,
    createUser,
    updateUserProfile,
    loading,
    logOut,
    handleLogout,
    signIn,
    providerLogin,
    devicelogOut,
    forgotPassword,
    setUser,
    auth,
    setLoading,
  };

  return (
    <div>
      <AuthContext.Provider value={authInfo}>{children}</AuthContext.Provider>
    </div>
  );
};

export default AuthProvider;
